import React, { useState, useEffect } from 'react';
import { TabContent, TabPane, Nav, NavItem, NavLink, Row, Col } from 'reactstrap';
import { formatTableDateTime } from '../../../constants/time';
import { showSwal } from '../../../constants/swalConfig';
import BreadCrumbs from '../../../layouts/breadcrumbs/BreadCrumbs';
import ComponentCard from '../../../components/ComponentCard';
import ActionButton from '../../../components/common/ActionButton';
import AppSpinner from '../../../components/common/AppSpinner';
import StatusBadge from '../../../components/common/StatusBadge';
import DataTable from '../../../components/common/DataTable';
import api from '../../../constants/api';

const STATUS = {
  0: { label: 'Pending', variant: 'danger' },
  1: { label: 'Converted', variant: 'success' },
  2: { label: 'Not Converted', variant: 'secondary' },
  4: { label: 'Progress', variant: 'warning' },
};

const BookingEnquiry = () => {
  const [activeTab, setActiveTab] = useState('1');
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const getAllEnquiries = () => {
    setLoading(true);
    api
      .get('/combined/enquiries/booking')
      .then((res) => {
        setEnquiries(res.data.data ? res.data.data.reverse() : []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getAllEnquiries();
  }, []);

  const deleteRecord = (table, column, value) => {
   showSwal({
  title: 'Are you sure?',
  text: "You won't be able to revert this!",
  icon: 'warning',
  showCancelButton: true,
  confirmButtonText: 'Yes, delete it!',
}, 'delete').then((result) => {
  if (result.isConfirmed) {
        api
          .post('/service/deleteRecord', { table, column, value })
          .then(() => {
            showSwal({
              title: 'Deleted!',
              text: 'Your file has been deleted.',
              icon: 'success',
            });
            window.location.reload();
          })
          .catch((err) => {
            console.log(err);
          });
      }
    });
  };

  const updateEnquiry = (id, status) => {
   showSwal({
  title: 'Are you sure?',
  text: '',
  icon: 'warning',
  showCancelButton: true,
  confirmButtonText: 'Confirm',
}).then((result) => {
  if (result.isConfirmed) {
    api
      .patch(`/website/updateWebsiteEnquiry/${id}`, { status })
      .then(() => {
        showSwal({
          title: 'Success!',
          text: '.',
          icon: 'success',
        });
        window.location.reload();
      })
          .catch((err) => {
            console.log(err);
          });
      }
    });
  };

  // Every tab shares the same shape; only the Action column differs per status.
  const buildColumns = (renderActions) => [
    { key: 'sno', header: 'S.No', render: (row, i) => i + 1 },
    { key: 'we_category', header: 'Category', sortable: true },
    { key: 'we_package', header: 'Package' },
    { key: 'we_name', header: 'Name', sortable: true },
    { key: 'we_phone', header: 'Phone' },
    { key: 'we_email', header: 'Email' },
    {
      key: 'we_date',
      header: 'Date Time',
      sortable: true,
      render: (row) => formatTableDateTime(row.we_date, row.we_time, true),
    },
    { key: 'we_notes', header: 'Notes' },
    {
      key: 'we_status',
      header: 'Status',
      render: (row) => {
        const status = STATUS[row.we_status];
        return status ? <StatusBadge variant={status.variant} label={status.label} /> : null;
      },
    },
    ...(renderActions ? [{ key: 'action', header: 'Action', render: renderActions }] : []),
  ];

  const editButton = (row, nextStatus) => (
    <ActionButton
      action="edit"
      onClick={() => {
        updateEnquiry(row.we_id, nextStatus);
      }}
    />
  );

  const deleteButton = (row) => (
    <ActionButton
      action="delete"
      onClick={() => {
        deleteRecord('website_enquiry', 'we_id', row.we_id);
      }}
    />
  );

  const pendingColumns = buildColumns((row) => (
    <div className="d-flex gap-2">
      {editButton(row, '4')}
      {deleteButton(row)}
    </div>
  ));

  const progressColumns = buildColumns((row) => (
    <div className="d-flex gap-2">
      {editButton(row, '1')}
      <ActionButton
        action="delete"
        onClick={() => {
          updateEnquiry(row.we_id, '2');
        }}
        title="Mark not converted"
      />
    </div>
  ));

  const convertedColumns = buildColumns(null);

  const notConvertedColumns = buildColumns((row) => (
    <div className="d-flex gap-2">
      {editButton(row, '1')}
      {deleteButton(row)}
    </div>
  ));

  const byStatus = (status) => (enquiries || []).filter((res) => res.we_status === status);

  const renderTable = (columns, status, emptyMessage) => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center py-4">
          <AppSpinner size="inline" label="Loading..." />
        </div>
      );
    }
    return (
      <DataTable
        columns={columns}
        mode="client"
        data={byStatus(status)}
        rowKey={(row) => row.we_id}
        searchPlaceholder="Search enquiries..."
        emptyMessage={emptyMessage}
      />
    );
  };

  return (
    <>
      <BreadCrumbs />
      <ComponentCard>
        <Nav tabs>
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={activeTab === '1' ? 'active' : ''}
              onClick={() => {
                toggle('1');
              }}
            >
              Pending
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={activeTab === '4' ? 'active' : ''}
              onClick={() => {
                toggle('4');
              }}
            >
              Progress
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={activeTab === '2' ? 'active' : ''}
              onClick={() => {
                toggle('2');
              }}
            >
              Converted
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={activeTab === '3' ? 'active' : ''}
              onClick={() => {
                toggle('3');
              }}
            >
              Not Converted
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent className="p-0" activeTab={activeTab}>
          <TabPane tabId="1">
            <Row>
              <Col sm="12">{renderTable(pendingColumns, '0', 'No pending enquiries found')}</Col>
            </Row>
          </TabPane>
          <TabPane tabId="4">
            <Row>
              <Col sm="12">{renderTable(progressColumns, '4', 'No enquiries in progress')}</Col>
            </Row>
          </TabPane>
          <TabPane tabId="2">
            <Row>
              <Col sm="12">{renderTable(convertedColumns, '1', 'No converted enquiries found')}</Col>
            </Row>
          </TabPane>
          <TabPane tabId="3">
            <Row>
              <Col sm="12">
                {renderTable(notConvertedColumns, '2', 'No not-converted enquiries found')}
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </ComponentCard>
    </>
  );
};

export default BookingEnquiry;
